import React, { useState, useRef, useEffect } from "react";
import { useLang } from "../context";
import { http } from "../api";
import { MessageCircle, X, Send } from "lucide-react";

const sid = Math.random().toString(36).slice(2);
const mkMsg = (role, text) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, role, text });

export const ChatWidget = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([mkMsg("assistant", lang === "fr"
        ? "Bonjour 👋 Je suis l'assistant ClassOne. Une question sur une pièce, une taille ou une commande ?"
        : "Hi 👋 I'm the ClassOne assistant. A question about a piece, a size or an order?")]);
    }
  }, [open, lang, messages.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, mkMsg("user", text)]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await http.post("/chat", { session_id: sid, message: text, lang });
      setMessages((m) => [...m, mkMsg("assistant", data.reply)]);
    } catch (e) {
      setMessages((m) => [...m, mkMsg("assistant", lang === "fr" ? "Désolé, une erreur est survenue." : "Sorry, an error occurred.")]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-toggle-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#1A1A1A] text-[#F9F8F6] flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
        {open ? <X size={22} strokeWidth={1.5} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[28rem] bg-[#F9F8F6] border border-[#1A1A1A]/15 rounded-md shadow-2xl flex flex-col overflow-hidden fade-up">
          <div className="px-4 py-3 border-b border-[#1A1A1A]/10">
            <div className="serif text-lg tracking-wide">ClassOne · {lang === "fr" ? "Assistant" : "Assistant"}</div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${m.role}`} className={`max-w-[80%] text-sm leading-relaxed px-3 py-2 rounded-md ${m.role === "user" ? "bg-[#1A1A1A] text-[#F9F8F6]" : "bg-[#E8E2D2] text-[#1A1A1A]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-[#737373]">…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#1A1A1A]/10 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="chat-input" placeholder={lang === "fr" ? "Écrivez votre message…" : "Type your message…"}
              className="flex-1 bg-transparent border border-[#1A1A1A]/20 rounded-sm px-3 py-2 text-sm outline-none focus:border-[#1A1A1A]" />
            <button onClick={send} data-testid="chat-send-btn" className="bg-[#1A1A1A] text-[#F9F8F6] px-3 rounded-sm">
              <Send size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
